import React, { useState } from 'react'
import { createUseStyles } from 'react-jss'
import { useAtom } from 'jotai'
import { AxiosResponse } from 'axios'
import { useParams } from 'react-router-dom'
import { CaseDetail } from '../../../../models/CaseDetail'
import { formatDate } from '../../../../utils/format'
import {
    Help,
    IkonContainerNyansattesoplysninger,
    IkonPromptfejl,
    IkonRedigeroplysninger,
} from '../../../../assets/icons'
import { FormControl } from '../../../ui/FormElememts/FormControl'
import { FormInputNumber } from '../../../modules/FormElements/FormInput'
import { Select } from '../../../ui/FormElememts/Select'
import Input from '../../../ui/FormElememts/Input'
import { Button } from '../../../ui/Button/Button'
import { CardColumn } from '../../../ui/Card/CardColumn'
import { colors } from '../../../../theme/palette'
import { L } from '../../../providers/Lokalise'
import { Typography } from '../../../ui/Typography/Typography'
import {
    updateCaseBirthdate,
    updateCaseEmail,
    updateCaseHireDate,
    updateCaseMobileNumber,
} from '../../../../api/salary'
import { profileAtom } from '../../../../api/profile/profile'
import { SimpleDialog } from '../../../ui/Dialog/SimpleDialog'
import {
    contactDialogStateAtom,
    ErrorDisplay,
    initialContactDialogState,
} from '../Contact/ContactDialog'
import { Tooltip } from '../../../ui/Tooltip/Tooltip'
import { caseDetailsUnsavedChangesAtom } from './CloseCaseRow'
import { DatePickerInput } from '../../../ui/FormElememts/DatePickerInput'
import { FormMessage } from '../../../ui/FormElememts/FormMessage'

const useStyles = createUseStyles({
    textWithIcon: {
        width: '1rem',
        height: '1rem',
        border: `1px solid ${colors.red600}`,
        borderRadius: '50%',
        marginLeft: '5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    icon: {
        width: '1rem',
        height: '1rem',
        borderRadius: '50%',
    },
    editButton: {
        color: colors.green600,
        fill: colors.green600,
        width: '2rem',
        height: '2rem',
        border: 'none',
        background: 'none',
        position: 'absolute',
        right: '0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        top: '0',
        bottom: '0',
        margin: 'auto',
        cursor: 'pointer',
        '&:disabled': {
            opacity: 0.7,
            color: colors.gray800,
            fill: colors.gray800,
            cursor: 'not-allowed',
        },
    },
    rowContainer: {
        position: 'relative',
        width: '100%',
        maxWidth: '100%',
    },
    buttonContainer: {
        display: 'flex',
        gap: '0.2rem',
        marginTop: '1rem',
        cursor: 'pointer',
        '& button': {
            flexGrow: 1,
            maxWidth: '50%',
        },
    },
})

export type CaseDetailsProps = {
    caseDetails: CaseDetail
    updateData: (data: CaseDetail) => void
}

const PesonalDetails = ({
    caseDetails,
    updateData,
}: CaseDetailsProps): JSX.Element => {
    const [authProfile] = useAtom(profileAtom)
    const classes = useStyles()
    const params = useParams()
    const [isLoading, setIsLoading] = useState(false)
    const [isMobileNumberEdit, setIsMobileNumberEdit] = useState(false)
    const [isEmailEdit, setIsEmailEdit] = useState(false)
    const [isBirthdateEdit, setIsBirthdayEdit] = useState(false)
    const [isHireDateEdit, setIsHireDateEdit] = useState(false)
    const [hireDateError, setHireDateError] = useState<{
        hasError: boolean
        message: string
        show: boolean
    }>({
        hasError: false,
        message: 'Datoen er ikke gyldig',
        show: false,
    })
    const [birthdateError, setBirthdateError] = useState<{
        hasError: boolean
        message: string
        show: boolean
    }>({
        hasError: false,
        message: 'Datoen er ikke gyldig',
        show: false,
    })
    const [selectedNumber, setSelectedNumber] = useState<string | null>(
        undefined
    )
    const [selectedEmail, setSelectedEmail] = useState<string | null>(undefined)
    const [selectedBirthdate, setSelectedBirthdate] = useState<string | null>(
        undefined
    )
    const [selectedHireDate, setSelectedHireDate] = useState<string | null>(
        undefined
    )
    const [errorMessage, setErrorMessage] = useState<React.ReactNode>('')

    const [openErrorDialog, setOpenErrorDialog] = useState<boolean>(false)
    const [contactDialogState, setContactDialogState] = useAtom(
        contactDialogStateAtom
    )
    const [caseDetailsUnsavedChanges, setCaseDetailsUnsavedChanges] = useAtom(
        caseDetailsUnsavedChangesAtom
    )

    const resetData = (): void => {
        setIsMobileNumberEdit(false)
        setSelectedNumber(null)
        setIsEmailEdit(false)
        setSelectedEmail(null)
        setIsBirthdayEdit(false)
        setSelectedBirthdate(null)
        setIsHireDateEdit(false)
        setSelectedHireDate(null)
    }
    const submitNumber = async (): Promise<AxiosResponse> => {
        const { id } = params
        const regTid = caseDetails?.caseRegistrationTime
        if (selectedNumber || selectedNumber === '') {
            setIsLoading(true)
            const response = await updateCaseMobileNumber(
                id,
                regTid,
                selectedNumber === '' ? null : selectedNumber ?? null
            )

            if (response.error) {
                setErrorMessage(<ErrorDisplay errors={response.error.errors} />)
                setOpenErrorDialog(true)
                setIsLoading(false)
            } else if (response.status === 200) {
                resetData()
                const details = CaseDetail.build(response.data)
                updateData(details)
            }
            setIsLoading(false)
        }
        return Promise.resolve({} as AxiosResponse)
    }
    const submitEmail = async (): Promise<AxiosResponse> => {
        const { id } = params
        const regTid = caseDetails?.caseRegistrationTime
        if (selectedEmail || selectedEmail === '') {
            setIsLoading(true)
            const response = await updateCaseEmail(
                id,
                regTid,
                selectedEmail === '' ? null : selectedEmail ?? null
            )
            if (response.error) {
                setErrorMessage(<ErrorDisplay errors={response.error.errors} />)
                setOpenErrorDialog(true)
                setIsLoading(false)
            } else if (response.status === 200) {
                resetData()
                const details = CaseDetail.build(response.data)
                updateData(details)
                setIsLoading(false)
            }
        }
        return Promise.resolve({} as AxiosResponse)
    }

    const correctFormat = (date: Date): string | null => {
        if (!date || Number.isNaN(date.getTime())) {
            return null
        }
        return `${date.getFullYear()}-${`0${date.getMonth() + 1}`.slice(
            -2
        )}-${`0${date.getDate()}`.slice(-2)}`
    }
    const submitBirthdate = async (): Promise<AxiosResponse> => {
        if (birthdateError.hasError) {
            setTimeout(() => {
                setBirthdateError((prev) => ({ ...prev, show: true }))
            }, 100)
            return Promise.resolve({} as AxiosResponse)
        }
        const { id } = params
        const regTid = caseDetails?.caseRegistrationTime
        const correctDateFormat = correctFormat(new Date(selectedBirthdate))
        setIsLoading(true)
        const response = await updateCaseBirthdate(
            id,
            regTid,
            selectedBirthdate === correctDateFormat
                ? null
                : correctDateFormat ?? null
        )
        if (response.error) {
            setErrorMessage(<ErrorDisplay errors={response.error.errors} />)
            setOpenErrorDialog(true)
            setIsLoading(false)
        } else if (response.status === 200) {
            // reset contactDialogStateAtom
            setContactDialogState(initialContactDialogState)
            resetData()
            const details = CaseDetail.build(response.data)
            updateData(details)
        }
        setIsLoading(false)
        return Promise.resolve({} as AxiosResponse)
    }
    const submitHireDate = async (): Promise<AxiosResponse> => {
        if (hireDateError.hasError) {
            setTimeout(() => {
                setHireDateError((prev) => ({ ...prev, show: true }))
            }, 100)
            return Promise.resolve({} as AxiosResponse)
        }
        const { id } = params
        const regTid = caseDetails?.caseRegistrationTime
        const correctDateFormat = correctFormat(new Date(selectedHireDate))

        setIsLoading(true)
        const response = await updateCaseHireDate(
            id,
            regTid,
            selectedHireDate === '' ? null : correctDateFormat ?? null
        )
        if (response.error) {
            setErrorMessage(<ErrorDisplay errors={response.error.errors} />)
            setOpenErrorDialog(true)
            setIsLoading(false)
        } else if (response.status === 200) {
            setContactDialogState(initialContactDialogState)
            resetData()
            const details = CaseDetail.build(response.data)
            updateData(details)
        }
        setIsLoading(false)
        return Promise.resolve({} as AxiosResponse)
    }

    const renderEditMobileEditButton = (): JSX.Element => {
        return (
            <button
                type="button"
                disabled={
                    !caseDetails?.allowedUpdateActionList.includes(
                        'Sagsbehandle'
                    ) || authProfile.isImitator
                }
                className={classes.editButton}
                onClick={() => {
                    setIsMobileNumberEdit(true)
                    setIsBirthdayEdit(false)
                    setIsHireDateEdit(false)
                    setIsEmailEdit(false)
                    if (
                        caseDetails?.newlyHired.contactInfo.mobileNumbers
                            .length === 1
                    ) {
                        setSelectedNumber(
                            caseDetails?.newlyHired.contactInfo.mobileNumbers[0]
                        )
                    }
                }}
            >
                {!caseDetails?.allowedUpdateActionList.includes(
                    'Sagsbehandle'
                ) ? (
                    <Tooltip
                        content={L(
                            'LØN_TOOLTIP_TAG_SAGEN_FOR_AT_BRUGE_FUNKTION'
                        )}
                    >
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                            <IkonRedigeroplysninger
                                style={{
                                    width: '1.5rem',
                                    height: '1.5rem',
                                }}
                            />
                        </span>
                    </Tooltip>
                ) : (
                    <IkonRedigeroplysninger
                        style={{
                            width: '1.5rem',
                            height: '1.5rem',
                        }}
                    />
                )}
            </button>
        )
    }

    const renderEditEmailButton = (): JSX.Element => {
        return (
            <button
                type="button"
                disabled={
                    !caseDetails?.allowedUpdateActionList.includes(
                        'Sagsbehandle'
                    ) || authProfile.isImitator
                }
                className={classes.editButton}
                onClick={() => {
                    setIsEmailEdit(true)
                    setIsBirthdayEdit(false)
                    setIsHireDateEdit(false)
                    setIsMobileNumberEdit(false)
                    if (
                        caseDetails?.newlyHired.contactInfo.emails.length === 1
                    ) {
                        setSelectedEmail(
                            caseDetails?.newlyHired.contactInfo.emails[0]
                        )
                    }
                }}
            >
                {!caseDetails?.allowedUpdateActionList.includes(
                    'Sagsbehandle'
                ) ? (
                    <Tooltip
                        content={L(
                            'LØN_TOOLTIP_TAG_SAGEN_FOR_AT_BRUGE_FUNKTION'
                        )}
                    >
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                            <IkonRedigeroplysninger
                                style={{
                                    width: '1.5rem',
                                    height: '1.5rem',
                                }}
                            />
                        </span>
                    </Tooltip>
                ) : (
                    <IkonRedigeroplysninger
                        style={{
                            width: '1.5rem',
                            height: '1.5rem',
                        }}
                    />
                )}
            </button>
        )
    }

    const renderBirthdateEditButton = (): JSX.Element => {
        return (
            <button
                type="button"
                disabled={
                    !caseDetails?.allowedUpdateActionList.includes(
                        'Sagsbehandle'
                    ) || authProfile.isImitator
                }
                className={classes.editButton}
                onClick={() => {
                    setIsBirthdayEdit(true)
                    setIsHireDateEdit(false)
                    setIsEmailEdit(false)
                    setIsMobileNumberEdit(false)
                    setSelectedBirthdate(caseDetails?.newlyHired.dateOfBirth)
                }}
            >
                {!caseDetails?.allowedUpdateActionList.includes(
                    'Sagsbehandle'
                ) ? (
                    <Tooltip
                        content={L(
                            'LØN_TOOLTIP_TAG_SAGEN_FOR_AT_BRUGE_FUNKTION'
                        )}
                    >
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                            <IkonRedigeroplysninger
                                style={{
                                    width: '1.5rem',
                                    height: '1.5rem',
                                }}
                            />
                        </span>
                    </Tooltip>
                ) : (
                    <IkonRedigeroplysninger
                        style={{
                            width: '1.5rem',
                            height: '1.5rem',
                        }}
                    />
                )}
            </button>
        )
    }

    const renderHireDateEditButton = (): JSX.Element => {
        return (
            <button
                type="button"
                disabled={
                    !caseDetails?.allowedUpdateActionList.includes(
                        'Sagsbehandle'
                    ) || authProfile.isImitator
                }
                className={classes.editButton}
                onClick={() => {
                    setIsHireDateEdit(true)
                    setIsBirthdayEdit(false)
                    setIsEmailEdit(false)
                    setIsMobileNumberEdit(false)
                    setSelectedHireDate(
                        caseDetails?.newlyHired.dateOfEmployment
                    )
                }}
            >
                {!caseDetails?.allowedUpdateActionList.includes(
                    'Sagsbehandle'
                ) ? (
                    <Tooltip
                        content={L(
                            'LØN_TOOLTIP_TAG_SAGEN_FOR_AT_BRUGE_FUNKTION'
                        )}
                    >
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                            <IkonRedigeroplysninger
                                style={{
                                    width: '1.5rem',
                                    height: '1.5rem',
                                }}
                            />
                        </span>
                    </Tooltip>
                ) : (
                    <IkonRedigeroplysninger
                        style={{
                            width: '1.5rem',
                            height: '1.5rem',
                        }}
                    />
                )}
            </button>
        )
    }

    return (
        <>
            <CardColumn
                titleLabel={L('LØN_NYANSATTES_OPLYSNINGER__CONTAINER_TITEL')}
                titleIcon={
                    <IkonContainerNyansattesoplysninger
                        style={{ color: colors.green600 }}
                        fill={colors.green}
                    />
                }
                rows={[
                    {
                        label: '', // Medlemsskab
                        value: '',
                        useContent: true,
                        content: (
                            <div>
                                <Typography
                                    tag="h4"
                                    size="XXS"
                                    color=" rgba(0, 0, 0, 0.4)"
                                >
                                    {L(
                                        'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_MEDLEMSSKAB_LABEL'
                                    )}
                                </Typography>
                                {caseDetails?.newlyHired
                                    .foaMemberDisplayText === 'Ikke fundet' ? (
                                    <span
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: colors.red600,
                                        }}
                                    >
                                        <span>
                                            {L(
                                                'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_MEDLEM_IKKE_FUNDET_LABEL'
                                            )}
                                        </span>
                                        <Tooltip
                                            content={L(
                                                'LØN_DETALJE_MEDLEM_IKKE_FUNDET_TOOLTIP'
                                            )}
                                        >
                                            <span
                                                className={classes.textWithIcon}
                                            >
                                                <Help
                                                    className={classes.icon}
                                                />
                                            </span>
                                        </Tooltip>
                                    </span>
                                ) : (
                                    <Typography tag="p" size="XS">
                                        {L(
                                            'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_MEDLEM_FUNDET_LABEL'
                                        )}
                                    </Typography>
                                )}
                            </div>
                        ),
                    },
                    {
                        label: '', // Mobil
                        value: '',
                        useContent: true,
                        content: (
                            <div className={classes.rowContainer}>
                                {isMobileNumberEdit ? (
                                    <FormControl>
                                        {caseDetails?.newlyHired.contactInfo
                                            .mobileNumbers.length > 1 &&
                                        typeof selectedNumber !== 'string' ? (
                                            <>
                                                <Select
                                                    // placeholder="Mobil"
                                                    placeholder={L(
                                                        'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_MOBIL_LABEL'
                                                    )}
                                                    onChange={(e) => {
                                                        setSelectedNumber(
                                                            e.target.value
                                                        )
                                                    }}
                                                    options={[
                                                        ...caseDetails?.newlyHired.contactInfo.mobileNumbers.map(
                                                            (number) => ({
                                                                label: number,
                                                                value: String(
                                                                    number
                                                                ),
                                                            })
                                                        ),
                                                        {
                                                            label: 'Andet',
                                                            value: '',
                                                        },
                                                    ]}
                                                />
                                                <div
                                                    className={
                                                        classes.buttonContainer
                                                    }
                                                >
                                                    <Button
                                                        buttonType="button"
                                                        type="secondary"
                                                        onClick={() => {
                                                            setIsMobileNumberEdit(
                                                                false
                                                            )
                                                            setSelectedNumber(
                                                                undefined
                                                            )
                                                            setCaseDetailsUnsavedChanges(
                                                                false
                                                            )
                                                        }}
                                                    >
                                                        {L(
                                                            'LØN_DETALJE_ANNULLER_KNAP'
                                                        )}
                                                    </Button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <FormInputNumber
                                                    name="mobil"
                                                    required={false}
                                                    // label="Mobil"
                                                    label={L(
                                                        'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_MOBIL_LABEL'
                                                    )}
                                                    value={selectedNumber}
                                                    onChange={(e) => {
                                                        setCaseDetailsUnsavedChanges(
                                                            true
                                                        )
                                                        setSelectedNumber(
                                                            e.target.value
                                                        )
                                                    }}
                                                />
                                                <div
                                                    className={
                                                        classes.buttonContainer
                                                    }
                                                >
                                                    <Button
                                                        type="secondary"
                                                        onClick={() => {
                                                            setIsMobileNumberEdit(
                                                                false
                                                            )
                                                            setSelectedNumber(
                                                                undefined
                                                            )
                                                            setCaseDetailsUnsavedChanges(
                                                                false
                                                            )
                                                        }}
                                                    >
                                                        {L(
                                                            'LØN_DETALJE_ANNULLER_KNAP'
                                                        )}
                                                    </Button>
                                                    <Button
                                                        type="primary"
                                                        loading={isLoading}
                                                        onClick={submitNumber}
                                                    >
                                                        {L(
                                                            'LØN_DETALJE_GEM_KNAP'
                                                        )}
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                    </FormControl>
                                ) : (
                                    <>
                                        <Typography
                                            tag="h4"
                                            size="XXS"
                                            color=" rgba(0, 0, 0, 0.4)"
                                        >
                                            {/* Mobil */}
                                            {L(
                                                'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_MOBIL_LABEL'
                                            )}
                                        </Typography>
                                        <Typography
                                            tag="p"
                                            size="XS"
                                            color={
                                                caseDetails?.newlyHired
                                                    ?.contactInfo?.mobileNumbers
                                                    ?.length > 1
                                                    ? colors.caseRed500
                                                    : colors.black
                                            }
                                        >
                                            {caseDetails?.newlyHired.contactInfo
                                                .mobileNumbers.length > 1
                                                ? L(
                                                      'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_FLERE_FUNDET_LABEL'
                                                  )
                                                : caseDetails?.newlyHired?.contactInfo?.mobileNumbers?.toString() ??
                                                  ''}
                                        </Typography>
                                        {caseDetails &&
                                            !caseDetails?.isClosed &&
                                            renderEditMobileEditButton()}
                                    </>
                                )}
                            </div>
                        ),
                    },
                    {
                        label: '', // Email
                        value: '',
                        useContent: true,
                        content: (
                            <div className={classes.rowContainer}>
                                {isEmailEdit ? (
                                    <FormControl>
                                        {caseDetails?.newlyHired.contactInfo
                                            .emails.length > 1 &&
                                        typeof selectedEmail !== 'string' ? (
                                            <>
                                                <Select
                                                    // placeholder="Email"
                                                    placeholder={L(
                                                        'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_EMAIL_LABEL'
                                                    )}
                                                    onChange={(e) => {
                                                        setSelectedEmail(
                                                            e.target.value
                                                        )
                                                    }}
                                                    options={[
                                                        ...caseDetails?.newlyHired.contactInfo.emails.map(
                                                            (address) => ({
                                                                label: address,
                                                                value: address,
                                                            })
                                                        ),
                                                        {
                                                            label: 'Andet',
                                                            value: '',
                                                        },
                                                    ]}
                                                />
                                                <div
                                                    className={
                                                        classes.buttonContainer
                                                    }
                                                >
                                                    <Button
                                                        buttonType="button"
                                                        type="secondary"
                                                        onClick={() => {
                                                            setIsEmailEdit(
                                                                false
                                                            )
                                                            setSelectedEmail(
                                                                undefined
                                                            )
                                                            setCaseDetailsUnsavedChanges(
                                                                false
                                                            )
                                                        }}
                                                    >
                                                        {L(
                                                            'LØN_DETALJE_ANNULLER_KNAP'
                                                        )}
                                                    </Button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <Input
                                                    required={false}
                                                    // label="Mobil"
                                                    label={L(
                                                        'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_EMAIL_LABEL'
                                                    )}
                                                    value={selectedEmail}
                                                    onChange={(e) => {
                                                        setCaseDetailsUnsavedChanges(
                                                            true
                                                        )
                                                        setSelectedEmail(
                                                            e.target.value
                                                        )
                                                    }}
                                                />
                                                <div
                                                    className={
                                                        classes.buttonContainer
                                                    }
                                                >
                                                    <Button
                                                        type="secondary"
                                                        onClick={() => {
                                                            setIsEmailEdit(
                                                                false
                                                            )
                                                            setSelectedEmail(
                                                                undefined
                                                            )
                                                            setCaseDetailsUnsavedChanges(
                                                                false
                                                            )
                                                        }}
                                                    >
                                                        {L(
                                                            'LØN_DETALJE_ANNULLER_KNAP'
                                                        )}
                                                    </Button>
                                                    <Button
                                                        type="primary"
                                                        loading={isLoading}
                                                        onClick={submitEmail}
                                                    >
                                                        {L(
                                                            'LØN_DETALJE_GEM_KNAP'
                                                        )}
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                    </FormControl>
                                ) : (
                                    <>
                                        <Typography
                                            tag="h4"
                                            size="XXS"
                                            color=" rgba(0, 0, 0, 0.4)"
                                        >
                                            {/* Email */}
                                            {L(
                                                'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_EMAIL_LABEL'
                                            )}
                                        </Typography>
                                        <Typography
                                            tag="p"
                                            size="XS"
                                            color={
                                                caseDetails?.newlyHired
                                                    ?.contactInfo?.emails
                                                    ?.length > 1
                                                    ? colors.caseRed500
                                                    : colors.black
                                            }
                                        >
                                            {caseDetails?.newlyHired.contactInfo
                                                .emails.length > 1
                                                ? L(
                                                      'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_FLERE_FUNDET_LABEL'
                                                  )
                                                : caseDetails?.newlyHired.contactInfo.emails.toString()}
                                        </Typography>
                                        {caseDetails &&
                                            !caseDetails?.isClosed &&
                                            renderEditEmailButton()}
                                    </>
                                )}
                            </div>
                        ),
                    },
                    {
                        label: '', // Fødselsdato
                        value: '',
                        useContent: true,
                        content: (
                            <div className={classes.rowContainer}>
                                {isBirthdateEdit ? (
                                    <FormControl>
                                        <DatePickerInput
                                            label={L(
                                                'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_FØDSELSDATO_LABEL'
                                            )}
                                            onChangeDateInputInvalid={(
                                                isValid,
                                                message
                                            ) => {
                                                setBirthdateError({
                                                    hasError: !isValid,
                                                    message:
                                                        message ??
                                                        'Datoen er ikke gyldig',
                                                    show: isValid
                                                        ? false
                                                        : birthdateError.show,
                                                })
                                            }}
                                            selected={
                                                selectedBirthdate !== null &&
                                                !!selectedBirthdate
                                                    ? new Date(
                                                          selectedBirthdate
                                                      )
                                                    : null
                                            }
                                            setSelected={(date) => {
                                                const d =
                                                    date === null
                                                        ? undefined
                                                        : date.toDateString()
                                                setCaseDetailsUnsavedChanges(
                                                    true
                                                )
                                                setSelectedBirthdate(d)
                                            }}
                                            minDate={new Date('1900-01-01')}
                                            maxDate={new Date()}
                                            hasError={
                                                birthdateError.hasError &&
                                                birthdateError.show
                                            }
                                        />
                                        {birthdateError.show && (
                                            <FormMessage
                                                text={birthdateError.message}
                                                error={birthdateError.message}
                                            />
                                        )}
                                        <div
                                            className={classes.buttonContainer}
                                        >
                                            <Button
                                                type="secondary"
                                                onClick={() => {
                                                    setIsBirthdayEdit(false)
                                                    setSelectedBirthdate(
                                                        undefined
                                                    )
                                                    setCaseDetailsUnsavedChanges(
                                                        false
                                                    )
                                                    setBirthdateError({
                                                        hasError: false,
                                                        message:
                                                            'Datoen er ikke gyldig',
                                                        show: false,
                                                    })
                                                }}
                                            >
                                                {L('LØN_DETALJE_ANNULLER_KNAP')}
                                            </Button>
                                            <Button
                                                type="primary"
                                                loading={isLoading}
                                                onClick={submitBirthdate}
                                            >
                                                {L('LØN_DETALJE_GEM_KNAP')}
                                            </Button>
                                        </div>
                                    </FormControl>
                                ) : (
                                    <>
                                        <Typography
                                            tag="h4"
                                            size="XXS"
                                            color=" rgba(0, 0, 0, 0.4)"
                                        >
                                            {/* Fødselsdato */}
                                            {L(
                                                'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_FØDSELSDATO_LABEL'
                                            )}
                                        </Typography>
                                        <Typography tag="p" size="XS">
                                            {caseDetails?.newlyHired
                                                ?.dateOfBirth &&
                                            caseDetails?.newlyHired
                                                ?.dateOfBirth !== null
                                                ? `${formatDate(
                                                      new Date(
                                                          caseDetails?.newlyHired.dateOfBirth
                                                      )
                                                  )} (${
                                                      caseDetails?.newlyHired
                                                          .age
                                                  } år)`
                                                : ''}
                                        </Typography>
                                        {caseDetails &&
                                            !caseDetails?.isClosed &&
                                            renderBirthdateEditButton()}
                                    </>
                                )}
                            </div>
                        ),
                    },
                    {
                        label: L(
                            'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_ARBEJDSSTED_LABEL'
                        ),
                        value: caseDetails?.newlyHired.workplace?.navn,
                    },
                    {
                        label: L(
                            'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_STILLING_LABEL'
                        ),
                        value: caseDetails?.newlyHired.position?.navn,
                    },
                    {
                        label: '',
                        value: '',
                        useContent: true,
                        content: (
                            <div className={classes.rowContainer}>
                                {isHireDateEdit ? (
                                    <FormControl>
                                        <DatePickerInput
                                            label={L(
                                                'LØN_NYANSATTES_OPLYSNINGER__CONTAINER_ANSTÆTTELSES_DATO_LABEL'
                                            )}
                                            selected={
                                                selectedHireDate !== null &&
                                                !!selectedHireDate
                                                    ? new Date(selectedHireDate)
                                                    : null
                                            }
                                            onChangeDateInputInvalid={(
                                                isValid,
                                                message
                                            ) => {
                                                setHireDateError({
                                                    hasError: !isValid,
                                                    message:
                                                        message ??
                                                        'Datoen er ikke gyldig',
                                                    show: isValid
                                                        ? false
                                                        : hireDateError.show,
                                                })
                                            }}
                                            setSelected={(date) => {
                                                const d =
                                                    date === null
                                                        ? undefined
                                                        : date.toDateString()
                                                setCaseDetailsUnsavedChanges(
                                                    true
                                                )
                                                setSelectedHireDate(d)
                                            }}
                                            minDate={new Date('1900-01-01')}
                                        />
                                        {hireDateError.show && (
                                            <FormMessage
                                                text={hireDateError.message}
                                                error={hireDateError.message}
                                            />
                                        )}
                                        <div
                                            className={classes.buttonContainer}
                                        >
                                            <Button
                                                type="secondary"
                                                onClick={() => {
                                                    setCaseDetailsUnsavedChanges(
                                                        false
                                                    )
                                                    setIsHireDateEdit(false)
                                                    setSelectedHireDate(
                                                        undefined
                                                    )
                                                    setHireDateError({
                                                        hasError: false,
                                                        message:
                                                            'Datoen er ikke gyldig',
                                                        show: false,
                                                    })
                                                }}
                                            >
                                                {L('LØN_DETALJE_ANNULLER_KNAP')}
                                            </Button>
                                            <Button
                                                type="primary"
                                                loading={isLoading}
                                                onClick={submitHireDate}
                                            >
                                                {L('LØN_DETALJE_GEM_KNAP')}
                                            </Button>
                                        </div>
                                    </FormControl>
                                ) : (
                                    <>
                                        <Typography
                                            tag="h4"
                                            size="XXS"
                                            color=" rgba(0, 0, 0, 0.4)"
                                        >
                                            Ansættelses dato
                                        </Typography>
                                        <Typography tag="p" size="XS">
                                            {caseDetails?.newlyHired
                                                .dateOfEmployment
                                                ? formatDate(
                                                      new Date(
                                                          caseDetails?.newlyHired.dateOfEmployment
                                                      )
                                                  )
                                                : ''}
                                        </Typography>
                                        {caseDetails &&
                                            !caseDetails?.isClosed &&
                                            renderHireDateEditButton()}
                                    </>
                                )}
                            </div>
                        ),
                    },
                ]}
            />
            {openErrorDialog && (
                <SimpleDialog
                    title="Fejl"
                    message={errorMessage}
                    closeDialog={() => setOpenErrorDialog(false)}
                    isError
                    icon={<IkonPromptfejl width="46px" fill={colors.red500} />}
                />
            )}
        </>
    )
}

export default PesonalDetails
